import React from "react";
import { useImmer } from "use-immer";
import { AppContext, TableStateContext } from "../../../store";
import { getEmailLogsApi, resendEmailApi } from "../apis";
import {
  convertDate,
  errorMessage,
  getDateRangeAsString,
  successMessage,
} from "../../../utils";
import moment from "moment";

const tableName = "email_log";

const defaultPagination = {
  pageIndex: 1,
  pageSize: 50,
  pageCount: 0,
  total: 0,
  hasMorePages: true,
  lastPage: 1,
};

const defaultDateObject = {
  isCalenderOpen: false,
  value: [
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ],
  showDate: [],
};

export const useEmailLogList = () => {
  const {
    appState: { globalData },
  } = React.useContext(AppContext);
  const { filterState, filterStateDispatch } =
    React.useContext(TableStateContext);

  const storedFilter = filterState.params[tableName];

  // Format the date values from the stored filter context, if available
  const storedDateValue =
    storedFilter?.appliedFilter?.date.value.length > 0
      ? [
          {
            // Convert the start and end dates from the stored filter to JavaScript Date objects
            startDate: moment(
              storedFilter.appliedFilter.date.value[0].startDate
            ).toDate(),
            endDate: moment(
              storedFilter.appliedFilter.date.value[0]?.endDate
            ).toDate(),
            key: "selection", // Use a fixed key for selection
          },
        ]
      : defaultDateObject.value;

  const [state, setState] = useImmer({
    emailLogs: [],
    pagination: storedFilter?.pagination
      ? storedFilter.pagination
      : defaultPagination,
    isLoading: false,
    sortBy: storedFilter?.sortBy ? storedFilter.sortBy : [],
    search: storedFilter?.search ?? "",
    filter: {
      options: {
        types: [],
        Statuses: [],
      },
      values: storedFilter?.appliedFilter?.values
        ? storedFilter.appliedFilter.values
        : { type: [], status: [] },
      date: storedFilter?.appliedFilter?.date
        ? {
            ...storedFilter.appliedFilter.date,
            value: storedDateValue,
            showDate:
              storedFilter.appliedFilter.date.showDate.length === 0 ? [] : storedDateValue,
          }
        : defaultDateObject,
      appliedFilters: [],
      isFilterApplied: true,
    },
    resendButtonLoading: null,
  });

  const breadcrumbs = [{ name: "Dashboard", href: "/" }, { name: "Email Log" }];

  const getEmailLogs = async (params, currentState) => {
    triggerTableLoading(true);
    try {
      const response = await getEmailLogsApi(params);
      const { success, message, data } = response;
      if (success) {
        filterStateDispatch({
          type: "SET_DATA",
          page: tableName,
          data: {
            appliedFilter: {
              date: currentState.filter.date,
              values: currentState.filter.values,
            },
            pagination: data.pagination,
            search: currentState.search,
            sortBy: currentState.sortBy,
          },
        });

        setState((draft) => {
          if (data?.pagination) draft.pagination = data.pagination;
          if (data?.email_log) draft.emailLogs = data.email_log;
        });
      } else {
        errorMessage(message);
      }
    } catch (error) {
    } finally {
      triggerTableLoading(false);
    }
  };

  const resendEmail = async (emailLogId, params) => {
    setResendButton(emailLogId);
    try {
      const response = await resendEmailApi(emailLogId);
      const { success, message } = response;
      if (success) {
        successMessage(message);
        await getEmailLogs({ ...params }, state);
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ?? "Failed to resend the email";
      errorMessage(message);
    } finally {
      setResendButton(null);
    }
  };

  //   ============================================================== UTIL FUNCTIONS ==============================================================
  const triggerTableLoading = (isLoading) => {
    setState((draft) => {
      draft.isLoading = isLoading;
    });
  };

  const setResendButton = (id) => {
    setState((draft) => {
      draft.resendButtonLoading = id;
    });
  };

  // Util function to handle navigation to first/last page of the table
  const gotoPage = async (page) => {
    setState((draft) => {
      draft.pagination.pageIndex = page;
    });

    const currentState = {
      ...state,
      pagination: { ...state.pagination, pageIndex: page },
    };
    const params = await getRequestUtilities(currentState);
    await getEmailLogs({ ...params }, currentState);
  };

  // Util function to handle table page-size changes
  const setPageSize = async (e) => {
    const pageSize = +e.target.value;
    const pageIndex = 1;

    setState((draft) => {
      draft.pagination.pageIndex = pageIndex;
      draft.pagination.pageSize = pageSize;
    });

    const currentState = {
      ...state,
      pagination: { ...state.pagination, pageIndex, pageSize },
    };

    const params = await getRequestUtilities(currentState);
    await getEmailLogs({ ...params }, currentState);
  };

  // Util function to handle table sort
  const handleSortBy = async (sortBy) => {
    setState((draft) => {
      draft.sortBy = sortBy;
    });

    const currentState = { ...state, sortBy };

    const params = await getRequestUtilities(currentState);
    await getEmailLogs({ ...params }, currentState);
  };

  const handleSearch = async (value) => {
    const pagination = { ...state.pagination, pageIndex: 1 };
    setState((draft) => {
      draft.search = value;
      draft.pagination = pagination;
    });

    const currentState = {
      ...state,
      pagination,
      search: value,
    };

    const params = await getRequestUtilities(currentState);
    await getEmailLogs({ ...params }, currentState);
  };

  const handleFilterChange = ({ value, type }) => {
    setState((draft) => {
      if (type === "date_range") {
        draft.filter.date.value = value;
      } else {
        draft.filter.values[type] = value;
        if (draft.filter.isFilterApplied) draft.filter.isFilterApplied = false;
      }
    });
  };

  const handleDateChangeApply = () => {
    setState((draft) => {
      draft.filter.date.showDate = state.filter.date.value;
      draft.filter.date.isCalenderOpen = false;
      draft.filter.isFilterApplied = false;
    });
  };

  const handleOpenDateRangeSelection = (e) => {
    setState((draft) => {
      draft.filter.date.isCalenderOpen = e;
    });
  };

  const getAppliedFilter = (appliedFilters) => {
    setState((draft) => {
      draft.filter.appliedFilters = appliedFilters;
    });
  };

  const handleApplyFilter = async (state) => {
    setState((draft) => {
      draft.filter.isFilterApplied = true;
    });
    const params = await getRequestUtilities(state);
    await getEmailLogs({ ...params }, state);
  };

  // Function to handle reset the specific filter
  const handleClearFilter = (event) => {
    setState((draft) => {
      if (event.accessorKey === "date_range") {
        draft.filter.date = defaultDateObject;
      } else {
        draft.filter.values[event.accessorKey] = draft.filter.values[
          event.accessorKey
        ].filter((item) => item.label !== event.label);
      }
      draft.filter.isFilterApplied = false;
    });
  };

  // Function to handle reset all filter
  const handleClearAllFilter = async () => {
    setState((draft) => {
      draft.filter.date = defaultDateObject;
      draft.filter.values = { status: [], type: [] };
    });

    const currentState = {
      ...state,
      filter: {
        ...state.filter,
        date: defaultDateObject,
        values: { status: [], type: [] },
      },
    };

    const params = await getRequestUtilities(currentState);
    await getEmailLogs({ ...params }, currentState);
  };

  const convertFilterToAppliedFilter = (appliedFilter) => {
    return Object.entries(appliedFilter)
      .flatMap(([key, value]) => {
        return Array.isArray(value)
          ? value.map((item) => ({
              type: key,
              label: item.label,
              accessorKey: key,
            }))
          : value.showDate[0]
          ? {
              type: key,
              label: getDateRangeAsString(value.showDate[0]),
              accessorKey: "date_range",
            }
          : undefined;
      })
      .filter(Boolean);
  };

  const getRequestUtilities = async (state) => {
    const params = {};
    const {
      pagination: { pageSize, pageIndex },
      filter,
      search,
      sortBy,
    } = state;

    // Pagination
    params.page = pageIndex;
    params.per_page = pageSize;

    // Table Search
    if (search) {
      params.search = search;
    }

    // Date filter
    if (filter.date.showDate[0]) {
      params.start_date = convertDate({
        date: filter.date.showDate[0].startDate,
      });
      params.end_date = convertDate({ date: filter.date.showDate[0].endDate });
    }

    // Type and Status filter
    Object.entries(filter.values).forEach(([key, values]) => {
      const actualKey = key === "type" ? "mail_class" : key;
      const appliedFilters = values.map((value) => value.value);
      if (appliedFilters.length > 0) {
        params[actualKey] = appliedFilters;
      }
    });

    // Column sort
    if (sortBy.length > 0) {
      params[`sort[${sortBy[0].id}]`] =
        sortBy[0].desc === true ? "desc" : "asc";
    }

    return params;
  };

  const handleResendEmail = async (emailLogId) => {
    const params = await getRequestUtilities(state);

    return await resendEmail(emailLogId, params);
  };

  //   ================================================================ USE EFFECTS =================================================================

  React.useEffect(() => {
    setState((draft) => {
      if (globalData?.email_log_status?.length > 0) {
        draft.filter.options.Statuses = globalData.email_log_status;
      }
      if (globalData?.mail_class?.length > 0) {
        draft.filter.options.types = globalData.mail_class;
      }
    });
  }, [globalData]);

  React.useEffect(() => {
    Promise.resolve(getRequestUtilities(state)).then((params) => {
      getEmailLogs({ ...params }, state);
    });
  }, []);

  React.useEffect(() => {
    getAppliedFilter(
      convertFilterToAppliedFilter({
        ...state.filter.values,
        date: state.filter.date,
      })
    );
  }, [state.filter.values, state.filter.date]);

  return {
    breadcrumbs,
    state,
    gotoPage,
    setPageSize,
    handleSortBy,
    handleSearch,
    triggerTableLoading,
    handleFilterChange,
    handleDateChangeApply,
    handleOpenDateRangeSelection,
    handleApplyFilter,
    handleClearAllFilter,
    handleClearFilter,
    handleResendEmail,
  };
};
